import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WhatsAppModelAPI } from '@bobex/react-core/domain/WhatsAppModel/WhatsAppModelAPI';
import { WhatsAppUnreadMessageView } from '@bobex/react-core/domain/WhatsAppModel/view/WhatsAppUnreadMessageView';
import { IconAlertCircle, IconBrandWhatsapp, IconUser } from '@tabler/icons-react';
import { PartnerRfpUtils } from '@bobex/react-core/domain/PartnerRfpModel/PartnerRfpUtils';
import { LanguageUtils } from '@bobex/react-core/utils/LanguageUtils';

interface WhatsAppDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  unreadCount: number;
}

export default function WhatsAppDrawer({ isOpen, onClose, unreadCount }: WhatsAppDrawerProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<WhatsAppUnreadMessageView[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial messages when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadUnreadMessages();
    }
  }, [isOpen]);

  const loadUnreadMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const msgs = await WhatsAppModelAPI.getUnreadMessages();
      setMessages(msgs);
    } catch (err) {
      console.error('Error loading unread messages:', err);
      setError(t('Unable to load WhatsApp messages'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const locale = LanguageUtils.getLocale(true) || 'en-US';
    const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    if (date.toDateString() === today.toDateString()) {
      return time;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `${t('Yesterday')} ${time}`;
    } else {
      return `${date.toLocaleDateString(locale)} ${time}`;
    }
  };

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap="xs" style={{ width: '100%' }}>
          <IconBrandWhatsapp size={20} color="#25D366" />
          <Text fw={600} size="sm" style={{ flex: 1 }}>
            WhatsApp
          </Text>
          {unreadCount > 0 && (
            <Badge
              color="#25D366"
              circle
              size="lg"
              style={{
                backgroundColor: '#25D366',
                color: 'white',
              }}
            >
              {unreadCount}
            </Badge>
          )}
        </Group>
      }
      padding={0}
      size="md"
      styles={{
        header: {
          backgroundColor: '#075E54',
          padding: '10px 12px',
          marginBottom: 0,
        },
        title: {
          width: '100%',
          color: 'white',
          fontSize: '14px',
        },
        close: {
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
        body: {
          padding: 0,
          height: 'calc(100vh - 60px)',
          backgroundColor: '#E5DDD5',
        },
      }}
    >
      <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Box p="sm">
            <Alert icon={<IconAlertCircle size={14} />} color="red" title={t('Error')}>
              {error}
            </Alert>
          </Box>
        )}

        {loading && (
          <Group justify="center" py="xl" style={{ flex: 1 }}>
            <Loader color="#25D366" />
          </Group>
        )}

        {!loading && messages.length === 0 && !error && (
          <Stack align="center" justify="center" style={{ flex: 1 }} gap="md">
            <IconBrandWhatsapp size={64} color="#B0B0B0" />
            <Text c="dimmed" ta="center" size="sm">
              {t('No unread WhatsApp messages')}
            </Text>
          </Stack>
        )}

        {!loading && messages.length > 0 && (
          <ScrollArea style={{ flex: 1 }} p="xs">
            <Stack gap={2}>
              {messages.map((message, index) => (
                <Paper
                  key={`${message.customerId}-${message.whatsappId}-${index}`}
                  p={0}
                  radius={0}
                  style={{
                    backgroundColor: 'white',
                    borderBottom: '1px solid #E5DDD5',
                  }}
                >
                  <Group
                    gap="sm"
                    p="xs"
                    align="flex-start"
                    wrap="nowrap"
                    style={{ marginBottom: 4 }}
                  >
                    <Avatar
                      name={`${message.customerName} ${message.customerLastName}`}
                      color="initials"
                      allowedInitialsColors={['#25D366', '#128C7E', '#075E54', '#34B7F1']}
                      radius="xl"
                      size="md"
                      style={{
                        flexShrink: 0,
                      }}
                    />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Group justify="space-between" mb={2} wrap="nowrap">
                        <Text
                          fw={600}
                          size="sm"
                          style={{
                            color: '#000',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {message.customerName} {message.customerLastName}
                        </Text>
                        <Text
                          size="xs"
                          style={{
                            color: '#667781',
                            flexShrink: 0,
                            marginLeft: '8px',
                          }}
                        >
                          {formatDate(message.lastMessageDate.toString())}
                        </Text>
                      </Group>
                      <Text
                        size="sm"
                        lineClamp={2}
                        style={{
                          color: '#667781',
                          lineHeight: 1.4,
                        }}
                      >
                        {message.lastMessageContent}
                      </Text>
                    </Box>
                  </Group>
                  <Group gap="xs" p="xs" pt={0} justify="flex-end">
                    <Button
                      component={Link}
                      to={PartnerRfpUtils.getEditLink(message.lastPartnerRfpId)}
                      variant="filled"
                      color="#25D366"
                      size="xs"
                      onClick={() => onClose()}
                    >
                      {t('Lead')}
                    </Button>
                    <Button
                      component={Link}
                      to={`/user/${message.customerId}/general`}
                      variant="outline"
                      color="#25D366"
                      size="xs"
                      leftSection={<IconUser size={14} />}
                      onClick={() => onClose()}
                    >
                      {t('Profile')}
                    </Button>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Box>
    </Drawer>
  );
}
